import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, SpecraFormField, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ClipboardCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { displayAnswer, parseAnswers, parseBasis, parseCriteria, parseSuggestions, useQualificationEvaluation, useQualificationEvents, useQualificationMutations, useQualificationVersions } from '../hooks/useQualification';
import { QualificationCriterion, QualificationFollowUp, qualificationCriterionTypes } from './QualificationCriterion';
import { QualificationHandoff } from './QualificationHandoff';
import { QualificationProfileEvidence } from './QualificationProfileEvidence';
import { criterionAnchor, QualificationSummary } from './QualificationSummaryPanel';
/**
 * Qualification for one canonical Opportunity (SAD-474): start or resume its
 * single evaluation, answer the pinned template version's criteria, and see the
 * disposition basis recorded at each defer / resume.
 */ export function QualificationSection({ opportunityId, pursuitName = 'Pursuit' }) {
    const { data: evaluation, isLoading, error } = useQualificationEvaluation(opportunityId);
    const { data: versions = [] } = useQualificationVersions();
    const { data: events = [] } = useQualificationEvents(evaluation?.id);
    const { start, saveAnswers, defer, resume, disposition, refreshSources, review } = useQualificationMutations(opportunityId);
    const [draft, setDraft] = useState({});
    const [choice, setChoice] = useState('');
    const [rationale, setRationale] = useState('');
    const savedAnswers = evaluation?.answersJson;
    useEffect(()=>setDraft(parseAnswers(savedAnswers)), [
        savedAnswers
    ]);
    const fail = (what)=>()=>toast.error(`Could not ${what}`);
    const pinned = versions.find((v)=>v.id === evaluation?.templateVersionId);
    const criteria = parseCriteria(pinned?.criteriaJson);
    const criterionTypes = qualificationCriterionTypes(pinned?.criteriaJson);
    const suggestions = parseSuggestions(evaluation?.suggestionsJson);
    const basis = parseBasis(evaluation?.answerBasisJson);
    const dispositioned = evaluation?.status === 'dispositioned';
    const latestDisposition = [
        ...events
    ].reverse().find((ev)=>ev.eventType === 'dispositioned');
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.qualification.section,
        "data-opportunity-id": opportunityId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-sm text-muted-foreground mb-2",
                children: [
                    /*#__PURE__*/ _jsx(ClipboardCheck, {
                        className: "h-4 w-4"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Qualification"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "ml-6 space-y-3 text-sm",
                children: [
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Loading…"
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Could not load qualification."
                    }),
                    !isLoading && !error && !evaluation && /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        disabled: start.isPending,
                        onClick: ()=>start.mutate(undefined, {
                                onError: fail('start evaluation')
                            }),
                        "data-testid": Testids.qualification.startButton,
                        children: "Start evaluation"
                    }),
                    evaluation && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        "data-testid": Testids.qualification.templateLabel,
                                        "data-template-version": evaluation.templateVersion,
                                        children: [
                                            evaluation.templateName,
                                            " v",
                                            evaluation.templateVersion
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        "data-testid": Testids.qualification.status,
                                        "data-evaluation-id": evaluation.id,
                                        "data-status": evaluation.status,
                                        children: evaluation.status
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    Object.values(criterionTypes).includes('strategic_fit') && /*#__PURE__*/ _jsx(QualificationProfileEvidence, {
                                        opportunityId: opportunityId
                                    }),
                                    criteria.map((c)=>/*#__PURE__*/ _jsx("div", {
                                            id: criterionAnchor(c.id),
                                            tabIndex: -1,
                                            children: /*#__PURE__*/ _jsxs(SpecraFormField, {
                                                label: c.label,
                                                children: [
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: [
                                                            basis[c.id]?.kind || (draft[c.id] ? 'manual_assessment' : 'unknown'),
                                                            basis[c.id]?.needsReview && /*#__PURE__*/ _jsxs("strong", {
                                                                children: [
                                                                    ' ',
                                                                    "— Needs review: upstream evidence changed; saved value retained."
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    criterionTypes[c.id] || draft[c.id] != null && typeof draft[c.id] === 'object' ? /*#__PURE__*/ _jsx(QualificationCriterion, {
                                                        id: c.id,
                                                        label: c.label,
                                                        type: criterionTypes[c.id] || 'open_question',
                                                        value: draft[c.id],
                                                        onChange: (value)=>setDraft((d)=>({
                                                                    ...d,
                                                                    [c.id]: value
                                                                }))
                                                    }) : /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            !draft[c.id] && /*#__PURE__*/ _jsx("p", {
                                                                children: "Unknown"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Input, {
                                                                value: displayAnswer(draft[c.id]),
                                                                onChange: (e)=>setDraft((d)=>({
                                                                            ...d,
                                                                            [c.id]: e.target.value
                                                                        })),
                                                                "aria-label": c.label,
                                                                "data-testid": Testids.qualification.answerInput,
                                                                "data-criterion-id": c.id
                                                            }),
                                                            !draft[c.id] && /*#__PURE__*/ _jsx(QualificationFollowUp, {
                                                                id: c.id,
                                                                label: c.label,
                                                                value: {
                                                                    state: 'unknown'
                                                                },
                                                                onChange: (value)=>setDraft((d)=>({
                                                                            ...d,
                                                                            [c.id]: value
                                                                        }))
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        }, c.id)),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                disabled: saveAnswers.isPending,
                                                onClick: ()=>saveAnswers.mutate({
                                                        evaluationId: evaluation.id,
                                                        answers: draft
                                                    }, {
                                                        onError: fail('save answers')
                                                    }),
                                                "data-testid": Testids.qualification.saveAnswersButton,
                                                children: saveAnswers.isPending ? 'Saving answers…' : 'Save answers'
                                            }),
                                            evaluation.status === 'active' && /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                disabled: defer.isPending,
                                                onClick: ()=>defer.mutate(evaluation.id, {
                                                        onError: fail('defer evaluation')
                                                    }),
                                                "data-testid": Testids.qualification.deferButton,
                                                children: "Defer"
                                            }),
                                            evaluation.status === 'deferred' && /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                disabled: resume.isPending,
                                                onClick: ()=>resume.mutate(evaluation.id, {
                                                        onError: fail('resume evaluation')
                                                    }),
                                                "data-testid": Testids.qualification.resumeButton,
                                                children: "Resume"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(QualificationSummary, {
                                criteria: criteria,
                                answers: parseAnswers(savedAnswers),
                                basis: basis,
                                suggestions: suggestions
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                "aria-label": "Disposition",
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("h4", {
                                        children: "Disposition"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "A person decides. The summary and any score are inputs, never the decision. Citing the summary items or evidence you relied on in the rationale keeps the basis clear."
                                    }),
                                    evaluation.reconsiderationNeeded && /*#__PURE__*/ _jsxs("output", {
                                        className: "block",
                                        "data-testid": Testids.qualification.reconsiderationFlag,
                                        children: [
                                            /*#__PURE__*/ _jsx("strong", {
                                                children: "Reconsider:"
                                            }),
                                            " answers or evidence changed after the latest disposition. The decision below is unchanged until you record a new one."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-2 max-w-md",
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                                "aria-label": "Disposition",
                                                value: choice,
                                                onChange: (e)=>setChoice(e.target.value),
                                                "data-testid": Testids.qualification.dispositionSelect,
                                                children: [
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "",
                                                        children: "Choose a disposition…"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "pursue",
                                                        children: "Pursue"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "no_bid",
                                                        children: "No bid"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "defer",
                                                        children: "Defer"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "watch",
                                                        children: "Watch"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "aria-label": "Rationale",
                                                placeholder: "Rationale (required)",
                                                value: rationale,
                                                onChange: (e)=>setRationale(e.target.value),
                                                "data-testid": Testids.qualification.dispositionRationale
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    size: "sm",
                                                    disabled: disposition.isPending || !choice || !rationale.trim(),
                                                    onClick: ()=>disposition.mutate({
                                                            evaluationId: evaluation.id,
                                                            disposition: choice,
                                                            note: rationale.trim()
                                                        }, {
                                                            onSuccess: ()=>{
                                                                setChoice('');
                                                                setRationale('');
                                                            },
                                                            onError: fail('record disposition')
                                                        }),
                                                    "data-testid": Testids.qualification.dispositionRecord,
                                                    children: dispositioned ? 'Record reconsideration' : 'Record disposition'
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            dispositioned && latestDisposition?.disposition === 'pursue' && /*#__PURE__*/ _jsx(QualificationHandoff, {
                                opportunityId: opportunityId,
                                evaluationId: evaluation.id,
                                pursuitName: pursuitName,
                                rationale: latestDisposition.note,
                                answersJson: latestDisposition.answersSnapshotJson
                            }),
                            !dispositioned && /*#__PURE__*/ _jsxs("section", {
                                "aria-label": "Source-backed suggestions",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex gap-2 items-center",
                                        children: [
                                            /*#__PURE__*/ _jsx("h4", {
                                                children: "Source-backed suggestions"
                                            }),
                                            /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                disabled: refreshSources.isPending,
                                                onClick: ()=>refreshSources.mutate(undefined, {
                                                        onError: fail('refresh source evidence; manual answers remain available')
                                                    }),
                                                "data-testid": Testids.qualification.refreshSources,
                                                children: "Refresh sources"
                                            })
                                        ]
                                    }),
                                    refreshSources.isPending && /*#__PURE__*/ _jsx("output", {
                                        children: "Checking evidence…"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "Review before use. Public prime amounts describe the prime award; they do not establish our subcontract share. Completion dates do not confirm solicitation deadlines. Manual answers work without AI or extraction."
                                    }),
                                    suggestions.length === 0 && /*#__PURE__*/ _jsx("p", {
                                        children: "No source suggestions yet. Refresh sources or continue manually."
                                    }),
                                    suggestions.map((suggestion)=>/*#__PURE__*/ _jsx(QualificationSuggestionCard, {
                                            suggestion: suggestion,
                                            disabled: review.isPending,
                                            onReview: (reviewAction, editedValueJson)=>review.mutate({
                                                    evaluationId: evaluation.id,
                                                    suggestionId: suggestion.id,
                                                    reviewAction,
                                                    editedValueJson
                                                }, {
                                                    onError: fail('review suggestion; refresh evidence and retry')
                                                })
                                        }, suggestion.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground mb-1",
                                        children: "Disposition basis history"
                                    }),
                                    events.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground",
                                        "data-testid": Testids.qualification.historyEmpty,
                                        children: "Nothing deferred or dispositioned yet."
                                    }) : /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-1",
                                        children: events.map((ev)=>/*#__PURE__*/ _jsxs("li", {
                                                "data-testid": Testids.qualification.historyItem,
                                                "data-event-type": ev.eventType,
                                                "data-disposition": ev.disposition,
                                                "data-template-version": ev.templateVersion,
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-medium",
                                                        children: ev.disposition ? `${ev.eventType}: ${ev.disposition}` : ev.eventType
                                                    }),
                                                    ' ',
                                                    "on v",
                                                    ev.templateVersion,
                                                    ev.createdAt && ` at ${new Date(Number(ev.createdAt.seconds) * 1000).toLocaleString()}`,
                                                    ev.note && /*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            ' — ',
                                                            /*#__PURE__*/ _jsx("span", {
                                                                "data-testid": Testids.qualification.historyRationale,
                                                                "data-actor": ev.actor,
                                                                children: ev.note
                                                            })
                                                        ]
                                                    }),
                                                    ":",
                                                    ' ',
                                                    /*#__PURE__*/ _jsx("span", {
                                                        "data-testid": Testids.qualification.historySnapshot,
                                                        children: Object.entries(parseAnswers(ev.answersSnapshotJson)).map(([k, v])=>`${k}: ${displayAnswer(v)}`).join('; ') || 'no answers'
                                                    })
                                                ]
                                            }, ev.id))
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function QualificationSuggestionCard({ suggestion, disabled, onReview }) {
    const [edited, setEdited] = useState('');
    const origin = Object.fromEntries(Object.entries(parseAnswers(suggestion.sourceJson)).map(([key, value])=>[
            key,
            displayAnswer(value)
        ]));
    let value = suggestion.valueJson;
    try {
        const parsed = JSON.parse(value);
        value = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
    } catch  {
    /* Keep source text visible. */ }
    const pending = suggestion.state === 'pending';
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded border p-2 my-2 space-y-2",
        "data-testid": Testids.qualification.suggestion,
        "data-suggestion-id": suggestion.id,
        "data-criterion-id": suggestion.criterionId,
        "data-review-state": suggestion.state,
        children: [
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        children: suggestion.criterionId
                    }),
                    ": ",
                    value,
                    " — ",
                    suggestion.evidenceState,
                    " /",
                    ' ',
                    suggestion.state
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs",
                "data-testid": Testids.qualification.sourceBasis,
                children: [
                    origin.origin,
                    " · ",
                    origin.kind,
                    " · version ",
                    origin.version || 'unknown',
                    " · as of",
                    ' ',
                    origin.asOf || 'unknown',
                    " · scope ",
                    origin.scope || 'unknown',
                    " · ",
                    origin.measure,
                    ' ',
                    origin.period,
                    " ",
                    origin.unit
                ]
            }),
            pending && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Input, {
                        "aria-label": `Edit suggested ${suggestion.criterionId}`,
                        placeholder: "Your assessment",
                        value: edited,
                        onChange: (e)=>setEdited(e.target.value),
                        "data-testid": Testids.qualification.suggestionEdit
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                disabled: disabled || suggestion.evidenceState !== 'available',
                                onClick: ()=>onReview('accepted'),
                                "data-testid": Testids.qualification.acceptSuggestion,
                                children: "Accept"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                disabled: disabled || !edited || suggestion.evidenceState !== 'available',
                                onClick: ()=>onReview('edited', JSON.stringify(edited)),
                                "data-testid": Testids.qualification.editSuggestion,
                                children: "Use edited answer"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                disabled: disabled,
                                onClick: ()=>onReview('rejected'),
                                "data-testid": Testids.qualification.rejectSuggestion,
                                children: "Reject"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
